import { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, KanbanSquare, FolderKanban, Sparkles, BarChart3, LogOut, Crown,
  UserCircle2, ExternalLink, Play, Clapperboard, Network, Wrench, LayoutGrid, MessageSquare,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FeedbackDialog from "@/components/FeedbackDialog";
import RoleChooser from "@/components/RoleChooser";

const PORTALS = [
  { to: "/app", label: "Hub", icon: LayoutGrid, end: true, key: "hub" },
  { to: "/app/watch", label: "Watch", icon: Play, key: "watch" },
  { to: "/app/studio", label: "Studio", icon: Clapperboard, key: "studio" },
  { to: "/app/network", label: "Network", icon: Network, key: "network" },
  { to: "/app/factory", label: "Factory", icon: Sparkles, key: "factory" },
  { to: "/app/tools", label: "Tools", icon: Wrench, key: "tools" },
];

const FACTORY_NAV = [
  { to: "/app/factory", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/factory/inbox", label: "Inbox", icon: MessageSquare },
  { to: "/app/factory/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/app/factory/projects", label: "Projects", icon: FolderKanban },
  { to: "/app/factory/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/app/factory/ai", label: "AI Studio", icon: Sparkles },
];

export default function AppShell() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inFactory = location.pathname.startsWith("/app/factory");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [closingFlow, setClosingFlow] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);

  useEffect(() => {
    if (user && !localStorage.getItem("iw_mode_chosen")) setRoleOpen(true);
  }, [user]);

  const toggleRole = async () => {
    const next = user.role === "admin" ? "influencer" : "admin";
    try {
      await api.setRole(next);
      await refresh();
      toast.success(`View switched to ${next.toUpperCase()}`);
    } catch { toast.error("Could not switch role"); }
  };

  const requestLogout = () => { setClosingFlow(true); setFeedbackOpen(true); };
  const openFeedback = () => { setClosingFlow(false); setFeedbackOpen(true); };
  const afterClosing = () => { if (closingFlow) logout(); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain">
      <header className="sticky top-0 z-30 glass border-b border-white/10">
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <Link to="/app" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
            <span className="flex flex-col justify-center leading-none">
              <span className="font-sans font-extrabold tracking-tight text-white text-lg">INDIEWOOD</span>
              <span className="mono text-[9px] text-kinetic tracking-[0.2em] mt-0.5">ARTIST TO AUDIENCE</span>
            </span>
            <span className="mono text-[9px] text-black bg-kinetic px-1.5 py-0.5 rounded-sm font-bold" data-testid="version-badge">TRIBE 1.0</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1 mx-auto">
            {PORTALS.map(p => (
              <NavLink
                key={p.key}
                to={p.to}
                end={p.end}
                data-testid={`portal-${p.key}`}
                className={({ isActive }) =>
                  `mono px-3 h-9 inline-flex items-center gap-2 rounded-sm transition-colors ${
                    (p.key === "factory" ? inFactory : isActive)
                      ? "text-kinetic bg-white/5" : "text-white/55 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <p.icon className="w-3.5 h-3.5" /> {p.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            <button data-testid="header-feedback-btn" onClick={openFeedback}
              className="hidden md:inline-flex mono px-3 h-8 rounded-sm border border-white/15 text-white/70 hover:border-kinetic hover:text-kinetic transition items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5" /> FEEDBACK
            </button>
            <NavLink to="/app/profile" data-testid="header-profile-link"
              className={({ isActive }) => `mono px-3 h-8 rounded-sm border inline-flex items-center gap-2 transition ${isActive ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/70 hover:border-kinetic hover:text-kinetic"}`}>
              <UserCircle2 className="w-3.5 h-3.5" /> PROFILE
            </NavLink>
            <div className="hidden md:flex items-center gap-2 mono text-white/50">
              <Crown className="w-3 h-3 text-kinetic" />
              <span data-testid="user-account-label">ACCOUNT</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="rounded-full ring-1 ring-white/10 hover:ring-kinetic transition">
                  <Avatar className="w-9 h-9 rounded-full">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-white/10 text-white">{user?.name?.[0] || "I"}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#0F0F0F] border-white/10 text-white">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-sans font-bold">{user?.name}</div>
                  <div className="mono text-white/40 mt-0.5 normal-case">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="nav-profile" onClick={() => navigate("/app/profile")} className="cursor-pointer focus:bg-white/10 focus:text-white">
                  <UserCircle2 className="w-4 h-4 mr-2" /> My profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-feedback" onClick={openFeedback} className="cursor-pointer focus:bg-white/10 focus:text-white">
                  <MessageSquare className="w-4 h-4 mr-2" /> Feedback &amp; inputs
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="toggle-role" onClick={toggleRole} className="cursor-pointer focus:bg-white/10 focus:text-white">
                  Switch view → {user?.role === "admin" ? "Influencer" : "Admin"}
                </DropdownMenuItem>
                {user?.slug && (
                  <DropdownMenuItem data-testid="open-public-link" onClick={() => window.open(`/u/${user.slug}`, "_blank")} className="cursor-pointer focus:bg-white/10 focus:text-white">
                    <ExternalLink className="w-4 h-4 mr-2" /> View public page
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="logout-btn" onClick={requestLogout} className="cursor-pointer focus:bg-white/10 focus:text-white">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile portal switcher */}
        <div className="lg:hidden border-t border-white/10 overflow-x-auto">
          <div className="flex gap-1 px-2 py-2 w-max">
            {PORTALS.map(p => (
              <NavLink key={p.key} to={p.to} end={p.end}
                className={({ isActive }) => `mono px-3 h-8 inline-flex items-center rounded-sm ${(p.key === "factory" ? inFactory : isActive) ? "text-kinetic bg-white/5" : "text-white/60"}`}>
                {p.label}
              </NavLink>
            ))}
          </div>
        </div>

        {/* Contextual Factory sub-nav */}
        {inFactory && (
          <div className="border-t border-white/10 bg-black/40 overflow-x-auto">
            <div className="flex gap-1 px-6 py-2 w-max">
              {FACTORY_NAV.map(n => (
                <NavLink key={n.to} to={n.to} end={n.end}
                  data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) => `mono px-3 h-8 inline-flex items-center gap-2 rounded-sm transition-colors ${isActive ? "text-kinetic bg-white/5" : "text-white/55 hover:text-white hover:bg-white/5"}`}>
                  <n.icon className="w-3 h-3" /> {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>
      <main className="px-6 py-8 max-w-[1600px] mx-auto">
        <Outlet />
      </main>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} closing={closingFlow} onDone={afterClosing} />
      <RoleChooser open={roleOpen} onOpenChange={setRoleOpen} />
    </div>
  );
}
